import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { AdminLayout, inp } from "@/pages/admin/adminShared";
import { formatDateShort } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Megaphone, Send, ShieldCheck, AlertTriangle } from "lucide-react";

const PERMS = [
  { key: "announce_manage", endpoint: "announce-manage", label: "Announcements", icon: Megaphone,
    desc: "post, edit and remove website announcements shown to the public and members." },
  { key: "sms_send", endpoint: "sms-send", label: "Emergency SMS", icon: Send,
    desc: "open the Emergency SMS management interface and prepare messages to members." },
  { key: "safety_access", endpoint: "safety-access", label: "Safety access", icon: ShieldCheck,
    desc: "view members' private emergency contact information in the restricted Member Safety area." },
];

export default function ManageUsers() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "admin";
  const { data: users, isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data });
  const [confirm, setConfirm] = useState(null); // { u, perm }

  const setRole = async (u, role) => {
    try { await api.put(`/admin/users/${u.id}/role`, { role }); qc.invalidateQueries({ queryKey: ["admin-users"] }); toast.success(`${u.first_name} is now ${role}`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const applyPerm = async (u, perm, granted) => {
    try {
      await api.put(`/admin/users/${u.id}/${perm.endpoint}`, { granted });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success(`${perm.label} ${granted ? "granted to" : "revoked from"} ${u.first_name}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const togglePerm = (u, perm) => {
    const currently = u[perm.key] === true;
    if (currently) applyPerm(u, perm, false); // revoke immediately, no confirm
    else setConfirm({ u, perm });             // confirm before granting
  };

  const confirmGrant = async () => {
    if (!confirm) return;
    await applyPerm(confirm.u, confirm.perm, true);
    setConfirm(null);
  };

  return (
    <AdminLayout title="Members" subtitle="Manage member roles and administrative permissions. Emergency contact details live in the restricted Member Safety area.">
      {isLoading ? <Spinner /> : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-[#6E5B51]"><tr>
              <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th>
              <th className="p-3">SMS alerts</th><th className="p-3">Joined</th><th className="p-3">Role</th>
              {isAdmin && <th className="p-3 min-w-[260px]">Permissions</th>}
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border last:border-0 align-top" data-testid={`user-row-${u.id}`}>
                  <td className="p-3 font-semibold text-[#2D1E18]">{u.name}</td>
                  <td className="p-3 text-[#5C4A42]">{u.email}</td>
                  <td className="p-3 text-[#5C4A42]">{u.phone_display || <span className="text-[#C9BDB4]">—</span>}</td>
                  <td className="p-3">{u.sms_opt_in ? (u.phone_is_mobile ? <span className="rounded-full bg-[#E8F0EC] px-2 py-0.5 text-xs font-bold text-[#4A6B5D]">Eligible</span> : <span className="rounded-full bg-[#FEF6EC] px-2 py-0.5 text-xs font-bold text-[#9A5B1E]">Opted in · no mobile</span>) : <span className="text-xs text-[#6E5B51]">No</span>}</td>
                  <td className="p-3 text-[#6E5B51]">{formatDateShort(u.created_at)}</td>
                  <td className="p-3">
                    <select className={`${inp} w-32`} value={u.role} onChange={(e) => setRole(u, e.target.value)} data-testid={`role-select-${u.id}`} disabled={!isAdmin}>
                      <option value="member">Member</option><option value="instructor">Instructor</option><option value="admin">Admin</option>
                    </select>
                  </td>
                  {isAdmin && (
                    <td className="p-3">
                      {(u.role === "instructor" || u.role === "admin") ? (
                        <div className="flex flex-wrap gap-1.5">
                          {PERMS.map((perm) => {
                            const on = u[perm.key] === true;
                            const Icon = perm.icon;
                            return (
                              <button key={perm.key} onClick={() => togglePerm(u, perm)} data-testid={`perm-${perm.key}-${u.id}`}
                                title={on ? `Revoke: ${perm.label}` : `Grant: ${perm.label}`}
                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${on ? "bg-[#C85A32] text-white" : "border border-border text-[#6E5B51] hover:border-[#C85A32]"}`}>
                                <Icon size={12} /> {perm.label}
                              </button>
                            );
                          })}
                        </div>
                      ) : <span className="text-xs text-[#C9BDB4]">Member — no permissions</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="grant-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-[#B04B26]" /> Grant a sensitive permission</DialogTitle>
          </DialogHeader>
          {confirm && (
            <div className="space-y-3 text-sm text-[#5C4A42]">
              <p>You are about to grant <span className="font-bold text-[#2D1E18]">{confirm.perm.label}</span> to{" "}
                <span className="font-bold text-[#2D1E18]">{confirm.u.name}</span>.</p>
              <p>This will allow them to {confirm.perm.desc}</p>
              <p className="rounded-xl bg-[#FEF6EC] px-3 py-2 text-xs text-[#9A5B1E]">This change takes effect immediately and is recorded in the Permission Audit log.</p>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setConfirm(null)} data-testid="grant-cancel" className="rounded-full border border-border px-4 py-2 text-sm font-semibold text-[#5C4A42]">Cancel</button>
            <button onClick={confirmGrant} data-testid="grant-confirm" className="rounded-full bg-[#C85A32] px-4 py-2 text-sm font-bold text-white hover:bg-[#B04B26]">Grant permission</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
